import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import { MaintenanceGate } from "@/components/Maintenance";
import { useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { DiscreetProvider } from "@/context/DiscreetContext";
import { Header } from "@/components/Header";
import { DisplayNameModal } from "@/components/DisplayNameModal";
import UpgradeModal from "@/components/UpgradeModal";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { BuildFreshness } from "@/components/BuildFreshness";
import { track } from "@/lib/analytics";

// V2 (SugarSense pivot) — bundled page components
import { ArchetypeBrowse, ArchetypeDetail } from "@/pages/ss/Archetypes";
import { ResearchFoundations } from "@/pages/ss/ResearchFoundations";
import { UnifiedSearch } from "@/pages/ss/Search";
import { Home, Pricing, About, Faq, Safety, Security, Contact, Essays, EssayDetail } from "@/pages/ss/Public";
import { Login, Register, ForgotPassword, SetPassword } from "@/pages/ss/Auth";
import {
  Dashboard, Ask, Profiles, ProfileDetail, ArchetypeBook,
  Timeline, Tracker, Drafts, Account, Billing, AdminLite, Onboarding,
} from "@/pages/ss/App";
import Intelligence from "@/pages/ss/Intelligence";
import FairUsage from "@/pages/ss/FairUsage";
import Calibration from "@/pages/ss/Calibration";
import SelfInsight from "@/pages/ss/SelfInsight";
import AmbassadorPortal from "@/pages/ss/AmbassadorPortal";

// Legal pages — preserved from v1 (NSW Australia approved)
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";

import AdminConsole from "@/pages/ss/AdminConsole";
import History, { HistoryDetail } from "@/pages/ss/History";
import ResetPassword from "@/pages/ResetPassword";

import "@/App.css";

function Shell() {
  const location = useLocation();
  const [limitDetail, setLimitDetail] = useState(null);
  useEffect(() => {
    // Fire page_view on every route change (privacy-safe: no body data).
    track("page_view");
  }, [location.pathname]);
  useEffect(() => {
    // Global plan-limit modal — any 402 with a known limit code anywhere
    // in the app surfaces this single top-level UpgradeModal, so we don't
    // have to wire 6 separate handlers in 6 components.
    const handler = (e) => setLimitDetail(e.detail || null);
    window.addEventListener("ss:limit-reached", handler);
    return () => window.removeEventListener("ss:limit-reached", handler);
  }, []);
  return (
    <MaintenanceGate>
      <Header />
      <DisplayNameModal />
      <UpgradeModal
        open={!!limitDetail}
        onClose={() => setLimitDetail(null)}
        detail={limitDetail}
      />
      <main key={location.pathname} className="ss-route-fade">
        <Outlet />
      </main>
    </MaintenanceGate>
  );
}

function App() {
  return (
    <div className="App" data-testid="app-root">
      <HelmetProvider>
        <AuthProvider>
          <DiscreetProvider>
            <BrowserRouter>
            <Routes>
              <Route element={<Shell />}>
                {/* Public */}
                <Route path="/" element={<Home />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/safety" element={<Safety />} />
                <Route path="/security" element={<Security />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/essays" element={<Essays />} />
                <Route path="/editorial" element={<Navigate to="/essays" replace />} />
                <Route path="/essays/:slug" element={<EssayDetail />} />
                <Route path="/archetypes" element={<ArchetypeBrowse />} />
                <Route path="/archetypes/:key" element={<ArchetypeDetail />} />
                <Route path="/research" element={<ResearchFoundations />} />
                <Route path="/search" element={<UnifiedSearch />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/fair-usage" element={<FairUsage />} />

                {/* Auth */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/set-password" element={<SetPassword />} />

                {/* App (protected) */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/intelligence" element={<ProtectedRoute><Intelligence /></ProtectedRoute>} />
                <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
                <Route path="/history/:kind/:id" element={<ProtectedRoute><HistoryDetail /></ProtectedRoute>} />
                <Route path="/calibration" element={<ProtectedRoute><Calibration /></ProtectedRoute>} />
                <Route path="/self-insight" element={<ProtectedRoute><SelfInsight /></ProtectedRoute>} />
                <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                <Route path="/ask" element={<ProtectedRoute><Ask /></ProtectedRoute>} />
                <Route path="/profiles" element={<ProtectedRoute><Profiles /></ProtectedRoute>} />
                <Route path="/profiles/:id" element={<ProtectedRoute><ProfileDetail /></ProtectedRoute>} />
                <Route path="/archetypes-book" element={<ProtectedRoute><ArchetypeBook /></ProtectedRoute>} />
                <Route path="/timeline" element={<ProtectedRoute><Timeline /></ProtectedRoute>} />
                <Route path="/tracker" element={<ProtectedRoute><Tracker /></ProtectedRoute>} />
                <Route path="/drafts" element={<ProtectedRoute><Drafts /></ProtectedRoute>} />
                <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
                <Route path="/ambassador" element={<ProtectedRoute><AmbassadorPortal /></ProtectedRoute>} />

                {/* Admin */}
                <Route path="/admin-ss" element={<ProtectedRoute adminOnly><AdminLite /></ProtectedRoute>} />
                <Route path="/admin-ss/:section" element={<ProtectedRoute adminOnly><AdminConsole /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLite /></ProtectedRoute>} />
              </Route>
            </Routes>
          </BrowserRouter>
          <BuildFreshness />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: "#FFF8F1",
                border: "1px solid #EBD9C0",
                color: "#3A2A2D",
                borderRadius: "10px",
              },
            }}
          />
        </DiscreetProvider>
      </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
